import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, ArrowRight, Filter } from "lucide-react";
import axios from "axios";
import { ArticlePlaceholder } from "@/components/ArticlePlaceholder";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Dynamic color mapping for categories
const getCategoryColor = (slug) => {
  const colors = {
    investment: "text-amber-400",
    company: "text-blue-400",
    market: "text-green-400",
    biotech: "text-purple-400",
  };
  return colors[slug] || "text-slate-400";
};

const getCategoryBgColor = (slug) => {
  const colors = {
    investment: "bg-amber-400/10 border-amber-400/30 text-amber-400",
    company: "bg-blue-400/10 border-blue-400/30 text-blue-400",
    market: "bg-green-400/10 border-green-400/30 text-green-400",
    biotech: "bg-purple-400/10 border-purple-400/30 text-purple-400",
  };
  return colors[slug] || "bg-slate-400/10 border-slate-400/30 text-slate-400";
};

export default function NewsPage() {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const location = useLocation();

  // Fetch news articles and categories on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [newsRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/news?limit=50`),
          axios.get(`${API}/news-categories`)
        ]);
        setArticles(newsRes.data);
        setCategories(categoriesRes.data);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Handle auto-open modal from homepage navigation
  useEffect(() => {
    if (location.state?.selectedArticleId && articles.length > 0) {
      const article = articles.find(a => a.id === location.state.selectedArticleId);
      if (article) {
        setSelectedArticle(article);
        // Clear the state to prevent re-opening on subsequent renders
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state?.selectedArticleId, articles]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Filter articles by selected category
  const filteredArticles = selectedCategory === "all" 
    ? articles 
    : articles.filter(article => article.category === selectedCategory);

  // Get unique categories from articles for the filter
  const articleCategories = [...new Set(articles.map(a => a.category))];

  return (
    <main data-testid="news-page" className="min-h-screen pt-20">
      {/* Hero */}
      <section className="py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-amber-400 uppercase tracking-[0.3em] text-xs mb-4"
          >
            Insights & Updates
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl text-white mb-6"
          >
            News & Analysis
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-slate-400 font-light text-base md:text-lg max-w-2xl"
          >
            Stay informed with the latest updates on our portfolio companies, 
            market insights, and investment perspectives.
          </motion.p>
        </div>
      </section>

      {/* News Grid */}
      <section className="px-6 md:px-12 lg:px-24 pb-24">
        <div className="max-w-7xl mx-auto">
          {/* Category Filters */}
          {!loading && articles.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mb-12"
            >
              <div className="flex items-center gap-3 mb-4">
                <Filter size={14} className="text-slate-500" />
                <span className="text-slate-500 text-xs uppercase tracking-wider">Filter by Category</span>
              </div>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => setSelectedCategory("all")}
                  data-testid="filter-all"
                  className={`px-4 py-2 text-xs uppercase tracking-wider border transition-all duration-300 ${
                    selectedCategory === "all"
                      ? "bg-amber-400/10 border-amber-400 text-amber-400"
                      : "border-white/10 text-slate-400 hover:border-white/30 hover:text-white"
                  }`}
                >
                  All ({articles.length})
                </button>
                {categories
                  .filter(cat => articleCategories.includes(cat.slug))
                  .map((category) => {
                    const count = articles.filter(a => a.category === category.slug).length;
                    return (
                      <button
                        key={category.id}
                        onClick={() => setSelectedCategory(category.slug)}
                        data-testid={`filter-${category.slug}`}
                        className={`px-4 py-2 text-xs uppercase tracking-wider border transition-all duration-300 ${
                          selectedCategory === category.slug
                            ? getCategoryBgColor(category.slug)
                            : "border-white/10 text-slate-400 hover:border-white/30 hover:text-white"
                        }`}
                      >
                        {category.name} ({count})
                      </button>
                    );
                  })}
              </div>
            </motion.div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-24">
              <div className="w-8 h-8 loading-spinner" />
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-24">
              <p className="text-slate-500">No news articles available at this time.</p>
            </div>
          ) : filteredArticles.length === 0 ? (
            <div className="text-center py-24">
              <p className="text-slate-500">No articles found in this category.</p>
              <button
                onClick={() => setSelectedCategory("all")}
                className="mt-4 text-amber-400 text-sm hover:underline"
              >
                View all articles
              </button>
            </div>
          ) : (
            <>
              {/* Results count */}
              <p className="text-slate-500 text-sm mb-6">
                Showing {filteredArticles.length} {filteredArticles.length === 1 ? 'article' : 'articles'}
                {selectedCategory !== "all" && (
                  <span> in <span className={getCategoryColor(selectedCategory)}>{selectedCategory}</span></span>
                )}
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredArticles.map((article, index) => (
                  <motion.article
                    key={article.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    data-testid={`news-article-${article.id}`}
                    className="group cursor-pointer"
                    onClick={() => setSelectedArticle(article)}
                  >
                    <div className="aspect-[16/10] overflow-hidden mb-5">
                      {article.image_url ? (
                        <img
                          src={article.image_url}
                          alt={article.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <ArticlePlaceholder className={`w-full h-full ${article.image_url ? 'hidden' : ''}`} />
                    </div>

                    <div className="flex items-center gap-3 mb-3">
                      <span 
                        className={`text-xs uppercase tracking-wider cursor-pointer hover:underline ${getCategoryColor(article.category)}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCategory(article.category);
                        }}
                      >
                        {article.category}
                      </span>
                      <span className="text-slate-600">•</span>
                      <span className="text-slate-500 text-xs flex items-center gap-1">
                        <Calendar size={12} />
                        {formatDate(article.published_at)}
                      </span>
                    </div>

                    <h2 className="text-white text-xl font-medium mb-3 group-hover:text-amber-400 transition-colors">
                      {article.title}
                    </h2>

                    <p className="text-slate-500 text-sm leading-relaxed line-clamp-3 mb-4">
                      {article.summary}
                    </p>

                    <span className="text-amber-400 text-xs uppercase tracking-wider flex items-center gap-2 group-hover:gap-3 transition-all">
                      Read More
                      <ArrowRight size={12} />
                    </span>
                  </motion.article>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Article Modal */}
      {selectedArticle && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          data-testid="article-modal-overlay"
          onClick={() => setSelectedArticle(null)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#0F172A] border border-white/10 max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="aspect-[21/9] overflow-hidden">
              {selectedArticle.image_url ? (
                <img
                  src={selectedArticle.image_url}
                  alt={selectedArticle.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <ArticlePlaceholder className={`w-full h-full ${selectedArticle.image_url ? 'hidden' : ''}`} />
            </div>

            <div className="p-8">
              <div className="flex items-center gap-3 mb-4">
                <span className={`text-xs uppercase tracking-wider ${getCategoryColor(selectedArticle.category)}`}>
                  {selectedArticle.category}
                </span>
                <span className="text-slate-600">•</span>
                <span className="text-slate-500 text-xs">
                  {formatDate(selectedArticle.published_at)}
                </span>
              </div>

              <h2 className="text-white text-2xl md:text-3xl font-medium mb-6">
                {selectedArticle.title}
              </h2>

              {/* Rich text content with HTML support */}
              <div 
                className="prose prose-invert prose-sm md:prose-base max-w-none text-slate-300 leading-relaxed
                  prose-headings:text-white prose-headings:font-medium prose-headings:mt-6 prose-headings:mb-4
                  prose-p:my-4 prose-p:leading-relaxed
                  prose-a:text-amber-400 prose-a:no-underline hover:prose-a:underline
                  prose-strong:text-white prose-em:text-slate-200
                  prose-ul:list-disc prose-ul:pl-6 prose-ol:list-decimal prose-ol:pl-6
                  prose-li:my-1
                  prose-blockquote:border-l-2 prose-blockquote:border-amber-400 prose-blockquote:pl-4 prose-blockquote:text-slate-400 prose-blockquote:italic
                  prose-img:rounded prose-img:my-4
                  prose-hr:border-white/10"
                dangerouslySetInnerHTML={{ __html: selectedArticle.content }}
              />

              {selectedArticle.source_url && (
                <a
                  href={selectedArticle.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-6 text-amber-400 text-sm hover:underline"
                >
                  Read original article
                  <ArrowRight size={14} />
                </a>
              )}

              <button
                onClick={() => setSelectedArticle(null)}
                className="mt-8 block text-slate-500 hover:text-amber-400 text-sm uppercase tracking-wider transition-colors"
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </main>
  );
}
